import MaintenanceOverlay, { MaintenanceOverlaySize } from "@components/utils/MaintenanceOverlay";
import MediaWithFallback from "@components/utils/MediaWithFallback";
import MemberModeProtector from "@components/utils/MemberModeProtector";
import { AuthSource } from "@configs/statsig";
import { ButtonIcon, GameModel, PropsWithAria, PropsWithClassName } from "kz-ui-sdk";
import NoSleep from "nosleep.js";
import { AspectRatio } from "react-aspect-ratio";
import { MdArrowForward } from "react-icons/md";

const noSleep = new NoSleep();
interface GameEntryProps extends PropsWithClassName, PropsWithAria {
  variant: "thumbnail" | "row";
  game: GameModel;
  providerName?: string;
  sourceRef?: string;
  onOpen: (game: GameModel) => void;
  maintenanceSize?: MaintenanceOverlaySize;
  maintenance?: boolean;
  isRecent?: boolean;
  onClickGameWrapper?: (game: GameModel) => void;
}

const GameEntry = ({
  game,
  onOpen,
  variant,
  providerName,
  sourceRef,
  maintenanceSize,
  maintenance,
  isRecent,
  onClickGameWrapper,
  ["aria-label"]: ariaLabel,
}: GameEntryProps) => {
  const handleOnClick = (e: React.MouseEvent) => {
    if (maintenance) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      noSleep.enable();
      onOpen?.(game);
      onClickGameWrapper?.(game);
    }
  };

  const gameEntryLabel = `${ariaLabel || "Game entry"} ${game.name}`;

  if (variant === "row") {
    return (
      <MemberModeProtector
        requireRegister
        registrationSource={AuthSource.GAME_ENTRY}
        onClick={() => onClickGameWrapper?.(game)}
      >
        <span
          className="flex items-center gap-x-4 overflow-hidden rounded-md"
          onClick={handleOnClick}
          role={"button"}
          aria-label={gameEntryLabel}
        >
          <div
            className="relative h-[62px] w-[62px] flex-shrink-0 overflow-hidden rounded-md bg-darkBrand-300"
            aria-label={gameEntryLabel}
          >
            <MaintenanceOverlay underMaintenance={maintenance}>
              <MediaWithFallback
                src={game.accountGame?.primaryImage?.url || game.primaryImage?.url}
                alt={game.name}
                aria-label={gameEntryLabel}
                loading="lazy"
              />
            </MaintenanceOverlay>
          </div>

          <div className="flex-grow items-start overflow-x-hidden">
            <h3
              className="overflow-x-hidden text-ellipsis whitespace-nowrap text-left text-base font-bold text-content-base"
              aria-label={gameEntryLabel}
            >
              {game.name}
            </h3>
            <p
              className="h-4 overflow-x-hidden overflow-y-hidden text-ellipsis whitespace-nowrap text-left text-xs text-content-soft"
              aria-label={gameEntryLabel}
            >
              {providerName}
            </p>
          </div>
          <ButtonIcon
            className="ml-auto !h-8 !w-8 flex-shrink-0"
            disabled={maintenance}
            variant="ghost"
            icon={<MdArrowForward className="scale-[0.8]" />}
            aria-label={gameEntryLabel}
          />
        </span>
      </MemberModeProtector>
    );
  }

  // default variant === "thumbnail"
  return (
    <AspectRatio ratio={1}>
      <MemberModeProtector
        requireRegister
        registrationSource={AuthSource.GAME_ENTRY}
      >
        <button
          className="relative aspect-square h-full w-full overflow-hidden rounded-md bg-darkBrand-500 bg-opacity-20"
          onClick={handleOnClick}
          aria-label={gameEntryLabel}
        >
          <MaintenanceOverlay
            underMaintenance={maintenance}
            size={maintenanceSize}
          >
            <MediaWithFallback
              src={game.accountGame?.primaryImage?.url || game.primaryImage?.url}
              alt={game.name}
              aria-label={gameEntryLabel}
              loading="lazy"
            />
          </MaintenanceOverlay>
        </button>
      </MemberModeProtector>
    </AspectRatio>
  );
};

export default GameEntry;
